import React from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowUp } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Footer = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigation = (sectionId: string) => {
    if (isHomePage) {
      // If on homepage, scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If on other pages, navigate to homepage with hash
      window.location.href = `/#${sectionId}`;
    }
  };

  const quickLinks = [
    { name: 'Home', sectionId: 'home' },
    { name: 'About Us', sectionId: 'about' },
    { name: 'Products', sectionId: 'products' },
    { name: 'Blog', href: '/blog' },
    { name: 'Quality Assurance', sectionId: 'certifications' },
    { name: 'Contact', sectionId: 'contact' }
  ];

  const products = [
    { name: 'Cotton Seed Cake', sectionId: 'products' },
    { name: 'Premium Khal', sectionId: 'products' },
    { name: 'Pellet Feed', sectionId: 'products' },
    { name: 'Dairy Concentrate', sectionId: 'products' },
    { name: 'Custom Formulations', sectionId: 'products' }
  ];

  const socialLinks = [
    { icon: Facebook, href: '#', color: 'hover:text-blue-600' },
    { icon: Twitter, href: '#', color: 'hover:text-blue-400' },
    { icon: Instagram, href: '#', color: 'hover:text-pink-600' },
    { icon: Linkedin, href: '#', color: 'hover:text-blue-700' }
  ];

  return (
    <footer className="bg-gradient-to-br from-amber-800 to-yellow-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-10 sm:mb-12">
          {/* Company Info */}
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center space-x-3">
              <img
                src="https://5.imimg.com/data5/SELLER/Logo/2024/6/429281295/JW/YO/WK/47161101/simran2-120x120.jpg"
                alt="Simran Industries Logo"
                className="w-10 sm:w-12 h-10 sm:h-12 rounded-full object-cover bg-white border border-amber-600 shadow"
              />
              <div>
                <h3 className="text-lg sm:text-2xl font-bold">Simran Industries</h3>
                <p className="text-yellow-200 text-xs sm:text-sm">Indore</p>
              </div>
            </div>
            <p className="text-yellow-100 text-sm leading-relaxed">
              Leading manufacturer of premium cotton seed cake and cattle feed in India.
              Trusted by dairy farmers nationwide since 1995.
            </p>
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  className={`w-9 sm:w-10 h-9 sm:h-10 bg-white/10 rounded-full flex items-center justify-center transition-all duration-300 ${social.color} hover:bg-white/20`}
                >
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Quick Links</h4>
            <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  {link.href ? (
                    <Link
                      to={link.href}
                      className="text-yellow-100 hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block"
                    >
                      {link.name}
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleNavigation(link.sectionId!)}
                      className="text-yellow-100 hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block text-left"
                    >
                      {link.name}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Our Products</h4>
            <ul className="space-y-2 sm:space-y-3 text-sm sm:text-base">
              {products.map((product, index) => (
                <li key={index}>
                  <button
                    onClick={() => handleNavigation(product.sectionId)}
                    className="text-yellow-100 hover:text-white transition-colors duration-300 hover:translate-x-1 inline-block text-left"
                  >
                    {product.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-4 sm:mb-6">Contact Info</h4>
            <div className="space-y-3 sm:space-y-4 text-sm">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <MapPin className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <p className="text-yellow-100 text-xs sm:text-sm">318/26, Bholaram Compound, Palda</p>
                  <p className="text-yellow-100 text-xs sm:text-sm">Indore, MP - 452001</p>
                </div>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <Phone className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <a href="tel:+919977724699" className="text-yellow-100 text-xs sm:text-sm hover:underline block">+91-9977724699</a>
                  <a href="tel:+919977765464" className="text-yellow-100 text-xs sm:text-sm hover:underline block">+91-9977765464</a>
                </div>
              </div>
              <div className="flex items-start space-x-2 sm:space-x-3">
                <Mail className="text-yellow-400 flex-shrink-0 mt-0.5" size={18} />
                <div>
                  <a href="mailto:simranindustries143@gmail.com" className="text-yellow-100 text-xs sm:text-sm hover:underline break-all">simranindustries143@gmail.com</a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-yellow-600 pt-8">
          <div className="flex flex-col-reverse sm:flex-row justify-between items-center space-y-4 space-y-reverse sm:space-y-0">
            <div className="text-center sm:text-left text-xs sm:text-sm">
              <p className="text-yellow-100">
                © 2025 Simran Industries, Indore. All rights reserved.
              </p>
              <p className="text-yellow-200 text-xs mt-1">
                Quality Feed Solutions Since 1995
              </p>
            </div>

            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className="group bg-yellow-400 text-amber-800 p-2 sm:p-3 rounded-full hover:bg-yellow-300 transition-all duration-300 hover:scale-110 shadow-lg"
              aria-label="Back to top"
            >
              <ArrowUp className="group-hover:-translate-y-1 transition-transform" size={18} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;